<script setup>
import { computed, ref, onUnmounted, nextTick } from 'vue';
import LiveKitAPI from 'dashboard/api/integrations/livekit';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { Room, RoomEvent, createLocalTracks } from 'livekit-client';

import { useMessageContext } from '../provider.js';
import BaseAttachmentBubble from './BaseAttachment.vue';

const { content, sender, id } = useMessageContext();

const { t } = useI18n();

const isLoading = ref(false);
const livekitToken = ref('');
const livekitUrl = ref('');
const roomName = ref('');
const room = ref(null);
const isConnected = ref(false);
const localVideoContainer = ref(null);
const remoteVideoContainer = ref(null);
const isCameraEnabled = ref(false);
const isMicEnabled = ref(false);
const isScreenSharing = ref(false);
const localVideoTrack = ref(null);
const localAudioTrack = ref(null);
const screenShareTrack = ref(null);
const hasRemoteParticipants = ref(false);
const isSwapped = ref(false);
const isFullscreen = ref(false);

const swapCameras = () => {
  isSwapped.value = !isSwapped.value;
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

const attachTrack = (track, container) => {
  if (!container) return;
  const element = track.attach();
  container.appendChild(element);
};

const joinTheCall = async () => {
  isLoading.value = true;
  try {
    console.log('Dashboard: Requesting to join meeting...', id.value);
    const { data: { token, livekit_url: url, room_name: name } = {} } =
      await LiveKitAPI.addParticipantToMeeting(id.value);
    console.log(
      'Dashboard: API response - token:',
      token,
      'url:',
      url,
      'room:',
      name
    );

    livekitToken.value = token;
    livekitUrl.value = url;
    roomName.value = name;

    // Connect to LiveKit room
    room.value = new Room();
    console.log('Dashboard: Room instance created');

    // Set up event listeners before connecting
    room.value.on(
      RoomEvent.TrackSubscribed,
      (track, publication, participant) => {
        console.log(
          'Dashboard: Track subscribed:',
          track.kind,
          'from:',
          participant.identity
        );
        if (track.kind === 'video' || track.kind === 'audio') {
          attachTrack(track, remoteVideoContainer.value);
          hasRemoteParticipants.value = true;
        }
      }
    );

    room.value.on(RoomEvent.TrackUnsubscribed, track => {
      console.log('Dashboard: Track unsubscribed:', track.kind);
      track.detach();
      // Check if there are still remote participants
      nextTick(() => {
        if (
          remoteVideoContainer.value &&
          !remoteVideoContainer.value.hasChildNodes()
        ) {
          hasRemoteParticipants.value = false;
        }
      });
    });

    console.log('Dashboard: Connecting to room...');
    await room.value.connect(url, token);
    console.log('Dashboard: Connected successfully!');
    isConnected.value = true;

    // Auto-enable microphone (but not camera - user will click to enable)
    console.log('Dashboard: Auto-enabling microphone...');
    await toggleMicrophone();
    console.log('Dashboard: Microphone enabled');
  } catch (err) {
    console.error('Dashboard LiveKit join error:', err);
    console.error('Dashboard error details:', {
      message: err.message,
      stack: err.stack,
      token: livekitToken.value,
      url: livekitUrl.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.LIVEKIT.JOIN_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const toggleCamera = async () => {
  if (!room.value) return;

  try {
    if (isCameraEnabled.value) {
      // Turn off camera
      if (localVideoTrack.value) {
        await room.value.localParticipant.unpublishTrack(localVideoTrack.value);
        localVideoTrack.value.stop();
        localVideoTrack.value = null;
      }
      isCameraEnabled.value = false;
    } else {
      // Turn on camera
      const tracks = await createLocalTracks({ video: true });
      const videoTrack = tracks[0];
      await room.value.localParticipant.publishTrack(videoTrack);
      localVideoTrack.value = videoTrack;

      await nextTick();
      if (localVideoContainer.value) {
        attachTrack(videoTrack, localVideoContainer.value);
      }
      isCameraEnabled.value = true;
    }
  } catch (err) {
    console.error('Camera toggle error:', err);
    useAlert('Failed to toggle camera');
  }
};

const toggleMicrophone = async () => {
  if (!room.value) return;

  try {
    if (isMicEnabled.value) {
      // Turn off microphone
      if (localAudioTrack.value) {
        await room.value.localParticipant.unpublishTrack(localAudioTrack.value);
        localAudioTrack.value.stop();
        localAudioTrack.value = null;
      }
      isMicEnabled.value = false;
    } else {
      // Turn on microphone
      const tracks = await createLocalTracks({ audio: true });
      const audioTrack = tracks[0];
      await room.value.localParticipant.publishTrack(audioTrack);
      localAudioTrack.value = audioTrack;
      isMicEnabled.value = true;
    }
  } catch (err) {
    console.error('Microphone toggle error:', err);
    useAlert('Failed to toggle microphone');
  }
};

const toggleScreenShare = async () => {
  if (!room.value) return;

  try {
    if (isScreenSharing.value) {
      // Stop screen sharing
      if (screenShareTrack.value) {
        await room.value.localParticipant.unpublishTrack(
          screenShareTrack.value
        );
        screenShareTrack.value.stop();
        screenShareTrack.value = null;
      }
      isScreenSharing.value = false;
    } else {
      // Start screen sharing using browser API with ultra high quality settings
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 3840, max: 7680 }, // 4K ideal, 8K max
          height: { ideal: 2160, max: 4320 },
          frameRate: { ideal: 60, max: 60 }, // 60fps
        },
        audio: false,
      });

      const screenTrack = stream.getVideoTracks()[0];
      await room.value.localParticipant.publishTrack(screenTrack, {
        videoEncoding: {
          maxBitrate: 8000000, // 8 Mbps for ultra high quality
          maxFramerate: 60,
        },
        simulcast: false, // Disable simulcast for better quality
      });
      screenShareTrack.value = screenTrack;

      // Listen for when user stops sharing via browser UI
      screenTrack.onended = () => {
        isScreenSharing.value = false;
        screenShareTrack.value = null;
      };

      await nextTick();
      if (localVideoContainer.value) {
        // Clear any existing video elements first
        const existingVideos =
          localVideoContainer.value.querySelectorAll('video');
        existingVideos.forEach(video => video.remove());

        const videoElement = document.createElement('video');
        videoElement.srcObject = stream;
        videoElement.autoplay = true;
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'contain';
        localVideoContainer.value.appendChild(videoElement);
      }
      isScreenSharing.value = true;
    }
  } catch (err) {
    console.error('Screen share toggle error:', err);
    if (err.name !== 'NotAllowedError') {
      useAlert('Failed to toggle screen share');
    }
  }
};

const leaveTheRoom = async () => {
  if (room.value) {
    await room.value.disconnect();
    room.value = null;
  }
  livekitToken.value = '';
  livekitUrl.value = '';
  roomName.value = '';
  isConnected.value = false;
  hasRemoteParticipants.value = false;
};

const action = computed(() => ({
  label: t('INTEGRATION_SETTINGS.LIVEKIT.CLICK_HERE_TO_JOIN'),
  onClick: joinTheCall,
}));

// Cleanup on unmount
onUnmounted(async () => {
  if (room.value) {
    await room.value.disconnect();
  }
});
</script>

<template>
  <BaseAttachmentBubble
    icon="i-ph-video-camera-fill"
    icon-bg-color="bg-[#2781F6]"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.MEETING"
    :action="action"
  >
    <div v-if="!sender" class="text-sm truncate text-n-slate-12">
      <!-- Added as a fallback, where the sender is not available (Deleted) -->
      <!-- Will show the content, if senderName in BaseAttachment.vue is empty -->
      {{ content }}
    </div>
    <div
      v-if="isConnected && room"
      class="video-call--container"
      :class="{ fullscreen: isFullscreen }"
    >
      <div class="livekit-room">
        <!-- Fullscreen toggle button -->
        <button
          class="fullscreen-btn"
          :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          @click="toggleFullscreen"
        >
          <i
            :class="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
          />
        </button>
        <!-- LiveKit video container -->
        <div class="participants-grid">
          <!-- Remote participants will be rendered here dynamically -->
          <div
            ref="remoteVideoContainer"
            :class="isSwapped ? 'local-participant' : 'participants-container'"
            @click="swapCameras"
          >
            <div
              v-if="!hasRemoteParticipants && !isSwapped"
              class="empty-state"
            >
              Waiting for others to join...
            </div>
          </div>
          <!-- Local participant video -->
          <div
            ref="localVideoContainer"
            :class="isSwapped ? 'participants-container' : 'local-participant'"
            @click="swapCameras"
          >
            <div
              v-if="!isCameraEnabled && !isSwapped"
              class="camera-off-placeholder"
            >
              <i class="i-lucide-user-circle text-6xl" />
              <span class="text-sm mt-2">Camera Off</span>
            </div>
          </div>
        </div>

        <!-- Control buttons (Google Meet style) -->
        <div class="controls-bar">
          <button
            class="control-btn"
            :class="[isMicEnabled ? 'active' : 'inactive']"
            :title="isMicEnabled ? 'Mute' : 'Unmute'"
            @click="toggleMicrophone"
          >
            <i
              :class="isMicEnabled ? 'i-lucide-mic' : 'i-lucide-mic-off'"
              class="control-icon"
            />
          </button>

          <button
            class="control-btn"
            :class="[isCameraEnabled ? 'active' : 'inactive']"
            :title="isCameraEnabled ? 'Turn off camera' : 'Turn on camera'"
            @click="toggleCamera"
          >
            <i
              :class="isCameraEnabled ? 'i-lucide-video' : 'i-lucide-video-off'"
              class="control-icon"
            />
          </button>

          <button
            class="control-btn"
            :class="[isScreenSharing ? 'active' : 'inactive']"
            :title="isScreenSharing ? 'Stop sharing' : 'Share screen'"
            @click="toggleScreenShare"
          >
            <i
              :class="
                isScreenSharing ? 'i-lucide-monitor-stop' : 'i-lucide-monitor'
              "
              class="control-icon"
            />
          </button>

          <button
            class="control-btn leave-btn"
            title="Leave call"
            @click="leaveTheRoom"
          >
            <i class="i-lucide-phone-off control-icon" />
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      {{ '' }}
    </div>
  </BaseAttachmentBubble>
</template>

<style lang="scss">
.join-call-button {
  margin: 0.5rem 0;
}

.video-call--container {
  position: relative;
  width: 100%;
  max-width: 900px;
  height: 500px;
  margin-top: 0.5rem;
  background: #000000;
  border-radius: 0.75rem;
  overflow: visible;
  transition: all 0.3s ease;

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    margin: 0;
    border-radius: 0;
    z-index: 9999;
  }

  .fullscreen-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.5rem;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: all 0.2s;

    i {
      font-size: 1.125rem;
      width: 1.125rem;
      height: 1.125rem;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.8);
      transform: scale(1.05);
    }
  }

  .livekit-room {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    border-radius: 0.75rem;

    .participants-grid {
      flex: 1;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr auto;
      gap: 1rem;
      padding: 1rem;
      position: relative;

      .participants-container {
        background: #1a1a1a;
        border-radius: 0.75rem;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        .empty-state {
          color: #a0a0a0;
          font-size: 1.125rem;
        }

        video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .local-participant {
        position: absolute;
        top: 1rem;
        left: 1rem;
        width: 180px;
        height: 135px;
        background: #1a1a1a;
        border-radius: 0.75rem;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #333;
        z-index: 5;
        cursor: move;

        .camera-off-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #a0a0a0;
        }

        video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }

    .controls-bar {
      position: absolute;
      bottom: 1rem;
      left: 10rem;
      display: flex !important;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      z-index: 10;

      .control-btn {
        width: 3.5rem;
        height: 3.5rem;
        border-radius: 50%;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
        position: relative;
        flex-shrink: 0;

        .control-icon,
        i {
          font-size: 1.25rem !important;
          width: 1.25rem !important;
          height: 1.25rem !important;
          display: inline-block !important;
          line-height: 1 !important;
        }

        &:hover {
          transform: scale(1.1);
        }

        &.active {
          background: #3b82f6 !important;
          color: white !important;
        }

        &.inactive {
          background: #ef4444 !important;
          color: white !important;
        }

        &.leave-btn {
          background: #dc2626 !important;
          color: white !important;

          &:hover {
            background: #b91c1c !important;
          }
        }
      }
    }
  }
}
</style>
